import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';

import { X } from 'lucide-react';

import { cn } from '~/utils/date';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 data-[state=open]:opacity-100 data-[state=closed]:opacity-0',
			className
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
	typeof DialogPrimitive.Content
> & {
	anchorRef?: React.RefObject<HTMLElement | null>;
	placement?: 'center' | 'below-anchor';
	offsetY?: number;
	disableOverlay?: boolean;
};

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	DialogContentProps
>(
	(
		{
			className,
			children,
			anchorRef,
			placement = 'center',
			offsetY = 8,
			disableOverlay,
			...props
		},
		ref
	) => {
		const [inlineStyle, setInlineStyle] = React.useState<
			React.CSSProperties | undefined
		>(undefined);

		React.useEffect(() => {
			if (placement !== 'below-anchor') {
				setInlineStyle(undefined);
				return;
			}

			const computePosition = () => {
				const anchorElement = anchorRef?.current as
					| HTMLElement
					| null
					| undefined;
				if (!anchorElement) return;
				const rect = anchorElement.getBoundingClientRect();
				const top = rect.bottom + offsetY;
				const left = rect.left + rect.width / 2;
				setInlineStyle({
					position: 'fixed',
					top,
					left,
					transform: 'translateX(-50%)',
				});
			};

			computePosition();
			window.addEventListener('resize', computePosition);
			window.addEventListener('scroll', computePosition, { passive: true });
			return () => {
				window.removeEventListener('resize', computePosition);
				window.removeEventListener('scroll', computePosition);
			};
		}, [placement, anchorRef, offsetY]);

		const baseCenteredClasses =
			'fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]';
		const baseBelowAnchorClasses = 'fixed';

		return (
			<DialogPortal>
				{!disableOverlay && <DialogOverlay />}
				<DialogPrimitive.Content
					ref={ref}
					style={inlineStyle}
					className={cn(
						`${placement === 'below-anchor' ? baseBelowAnchorClasses : baseCenteredClasses} z-50 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg sm:rounded-lg transition-[opacity,transform] duration-300 data-[state=open]:opacity-100 data-[state=open]:scale-100 data-[state=closed]:opacity-0 data-[state=closed]:scale-95`,
						className
					)}
					{...props}
				>
					{children}
					<DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
						<X className="h-4 w-4" />
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogPortal>
		);
	}
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'flex flex-col space-y-1.5 text-center sm:text-left',
			className
		)}
		{...props}
	/>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
			className
		)}
		{...props}
	/>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			'text-lg font-semibold leading-none tracking-tight',
			className
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogClose,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
};
